"use strict"

import React, { useEffect, useState, useRef, useCallback } from "react"

import { Modal, Button } from "react-bootstrap"
import html2canvas from "html2canvas"

import { isNumber, triggerDownload } from "../../utils"
import KeyBody from "../plotting/GraphKeys/KeyBody"
import { makeXAxis } from "../resources/utils"
import FontEditPopover from "../plotting/widgets/FontEditPopover"
import GraphSizeEditPopover from "../plotting/widgets/GraphSizeEditPopover"
import RunEventLabelLegend from "../plotting/widgets/ToggleRunLabelsButton/RunEventLabelLegend"
import { isLTPM, LegendTimeseriesClassification } from "../../../components/shared_components_registration"

const registration = {
  download_graph_image_title: (intl) =>
    isLTPM ? intl.formatMessage({ id: "graphing.download.graph.image" }) : "Download Graph Image",
  download_label: (intl) => (isLTPM ? intl.formatMessage({ id: "general.download" }) : "Download"),
  allow_watermark_show: !isLTPM,
  watermark_image: isLTPM ? null : "/static/images/logos/logo_short_grey.png", // TODO - Will need if want to show LTPM watermark
  show_watermark: (intl) => (isLTPM ? "" : "Show Watermark"), // TODO - Will need if want to show LTPM watermark
  hide_watermark: (intl) => (isLTPM ? "" : "Hide Watermark"), // TODO - Will need if want to show LTPM watermark
}

const getWidth = (element, wOffset) => Math.max(element.clientWidth - wOffset, 500)
const getHeight = (width) => (width / 8) * 3

export const useGraphImageState = (
  extraOnResize,
  timeExtractor,
  baseTime,
  minTime,
  maxTime,
  single,
  offset = 0,
  maxFullTime,
  wOffset = 400,
) => {
  const element = document.getElementById("application-page")
  const [dim, setDim] = useState({
    width: getWidth(element, wOffset),
    height: getHeight(getWidth(element, wOffset) * 0.75),
  })
  const [defaultDim, setDefaultDim] = useState({
    width: getWidth(element, wOffset),
    height: getHeight(getWidth(element, wOffset) * 0.75),
  })

  // add listener for window resize
  useEffect(() => {
    const handleResize = () => {
      const newWidth = getWidth(element, wOffset)
      const newHeight = getHeight(newWidth * 0.75)
      setDim({
        width: newWidth,
        height: newHeight,
      })
      setDefaultDim({
        width: newWidth,
        height: newHeight,
      })

      extraOnResize && extraOnResize(newHeight)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const x = makeXAxis(
    timeExtractor,
    timeExtractor.offsetDays(baseTime, (minTime || 0) + offset),
    single
      ? maxTime || isNumber(maxTime)
        ? timeExtractor.offsetDays(baseTime, maxTime)
        : maxFullTime
      : timeExtractor.offsetDays(baseTime, (maxTime || isNumber(maxTime) ? maxTime : maxFullTime) + offset),
    0,
    dim.width,
  )

  return [x, dim, setDim, defaultDim]
}

export const GraphImageLegend = ({
  intl,
  graphKey,
  graphIndex,
  plotEntry,
  observationTypes,
  crossPlotProps,
  width = "75%",
  runEventLabels = [],
}) => {
  return (
    <div
      style={{
        marginTop: "3px",
        overflow: "hidden",
        width: width,
        margin: "0 auto",
      }}
    >
      <KeyBody
        intl={intl}
        graphKey={graphKey}
        index={graphIndex}
        plotEntry={plotEntry}
        observationTypes={observationTypes}
        crossPlotProps={crossPlotProps}
        download
      />

      <LegendTimeseriesClassification />

      <RunEventLabelLegend runEventLabels={runEventLabels} />
    </div>
  )
}

/*
  Started the process of moving away from needing html2canvas to generate images by passing a reference to any canvas
  elements created (modalCanvasRef). This method can only be fully complete if we were to draw ALL parts of the expected
  export image in canvas. Axes, axes labels, title, and the graph key are NOT written into canvas right now so even if
  we keep track of a passed reference to the trend plotting canvas here we must still rely on packages like html2canvas
  to actually concatenate the other parts of the HTML into a cohesive and final image. Ideally we would draw everything
  into canvas and forego the need to run an html2canvas-like export so instead of using method exportViaHtmlToCanvas
  (can be prone to timeouts/out-of-memory errors for particularly large and data heavy images) we would use
  directCanvasExport (already have canvas in hand; just call .toDataURL() method and avoid costly canvas creation on
  export)
 */
export default function GraphImageModal(props) {
  const {
    onHide,
    plotEntry,
    title = "",
    observationTypes,
    minTime,
    maxTime,
    maxFullTime,
    timeExtractor,
    baseTime,
    graphKey,
    graphIndex,
    crossPlotProps,
    plotProps,
    offset = 0,
    single,
    intl,
    runEventLabels = [],
    useSVG = false, // Will draw the image using SVG (not canvas) if true
  } = props
  const plotFactory = plotEntry.plotFactory

  // reference for modal body
  const modalBodyRef = useRef(null)

  // reference for modal <canvas> element
  const modalCanvasRef = useRef(null)

  const [showWaterMark, setShowWaterMark] = useState(false)
  const [watermarkDim, setWatermarkDim] = useState({
    height: 500,
    top: 293,
    left: 250,
  })

  const [x, dim, setDim, defaultDim] = useGraphImageState(
    setWatermarkDim,
    timeExtractor,
    baseTime,
    minTime,
    maxTime,
    single,
    offset,
    maxFullTime,
  )

  const updateWatermarkDim = useCallback((newHeight) => {
    const waterMarkHeight = (newHeight / 520) * 500
    setWatermarkDim({
      height: waterMarkHeight,
      top: (waterMarkHeight / 520) * 300,
      left: (waterMarkHeight / 520) * 250,
    })
  }, [])

  useEffect(() => {
    updateWatermarkDim(dim?.height || 500)
  }, [dim])

  const onSubmit = () => {
    if (modalBodyRef.current && modalCanvasRef.current) {
      /*
        Canvas reference tracking path.

        Doesn't currently do anything exciting beyond show it is possible to keep track of a passed canvas reference
        (modalCanvasRef.current). We still require html2canvas to concatenate with the other graph elements even if we
        still have a handle on modalCanvasRef as modalCanvasRef is only the canvas containing the trends/gridlines.
      */
      exportViaHtmlToCanvas(modalBodyRef.current, title)
      onHide()
    } else {
      /*
        SVG element path. Support image exports for graphs without a canvas drawing option.

        Same logic as above 'if', but listed separately as the long term goal of the 'if' should be to be able to export
        a fully fledged canvas element without requiring html2canvas as an intermediary for canvas based components.
       */
      exportViaHtmlToCanvas(modalBodyRef.current, title)
      onHide()
    }
  }

  return (
    <Modal show onHide={onHide} style={{ overflowX: "scroll" }} backdrop="static" enforceFocus={false}>
      <Modal.Header closeButton>
        <h3>{registration.download_graph_image_title(intl)}</h3>
      </Modal.Header>
      <Modal.Body>
        {/* Toolbar - any image adjustments/forms/buttons here */}
        <div style={{ display: "flex" }}>
          <div style={{ marginLeft: "auto" }}>
            <FontEditPopover intl={intl} />
            <GraphSizeEditPopover defaultDim={defaultDim} dim={dim} setDim={setDim} intl={intl} />
          </div>
        </div>
        {/* Contents of this div are exported to PNG */}
        <div ref={modalBodyRef}>
          <h3 style={{ textAlign: "center" }}>{title.replace(/_/g, " ")}</h3>
          <div style={{ position: "relative" }}>
            {registration.allow_watermark_show && showWaterMark && registration.watermark_image && (
              <img
                src={registration.watermark_image}
                style={{
                  height: `${watermarkDim.height}px`,
                  position: "absolute",
                  top: `calc(50% - ${watermarkDim.top}px)`,
                  left: `calc(50% - ${watermarkDim.left}px)`,
                  opacity: "0.075",
                }}
              />
            )}
            <plotFactory.Component
              {...props}
              {...plotProps}
              {...crossPlotProps}
              ref={modalCanvasRef}
              x={x}
              width={dim.width}
              height={dim.height}
              margin={{ top: 20, bottom: 80, left: 80, right: 85 }}
              useSVG={useSVG} // If true no canvas element will be created
              download
            />
          </div>
          {/*----- Keys & Legend -----*/}
          <div style={{ position: "relative" }}>
            <GraphImageLegend
              intl={intl}
              graphKey={graphKey}
              graphIndex={graphIndex}
              plotEntry={plotEntry}
              observationTypes={observationTypes}
              crossPlotProps={crossPlotProps}
              runEventLabels={runEventLabels}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {registration.allow_watermark_show && (
          <Button
            onClick={(e) => {
              e.preventDefault()
              setShowWaterMark(!showWaterMark)
            }}
          >
            {showWaterMark ? registration.hide_watermark(intl) : registration.show_watermark(intl)}
          </Button>
        )}
        <Button variant="primary" onClick={onSubmit}>
          {registration.download_label(intl)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

/*
  Method to export a complete canvas directly (skipping the need for htmltocanvas conversion).
  This would be ideal if the ENTIRE modal body export image was in a canvas rather than being only partially or not at
  all. Either way there are elements of the key/axis labels that are NOT canvas so until we draw them all out manually
  we still have to use some type of html to canvas dump. Even if we can pass/grab the canvas ref for the main body of
  the graph image. That would be perfect once we have everything in canvas.
 */
const directCanvasExport = (canvas, title) => {
  triggerDownload(canvas.toDataURL(), `${title}.png`)
}

/*
  Build a canvas from HTML using html2canvas and then export. Current version of the code doesn't put everything in
  canvas (axes, axes labels, graph key), so in order to get those in the export image we need to do an HTML to canvas
  dump via a package like html2canvas (can be prone to timeouts/out-of-memory errors for particularly large images).
 */
const exportViaHtmlToCanvas = (node, title) => {
  html2canvas(node).then((canvas) => triggerDownload(canvas.toDataURL(), `${title}.png`))
}
